import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Gift } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const refCode = (search.get("ref") || "").trim().toUpperCase();

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [referrerName, setReferrerName] = useState(null);

    // Look up who referred them so we can show a friendly "invited by <name>" banner.
    useEffect(() => {
        if (!refCode) return;
        api.get(`/referral/lookup/${encodeURIComponent(refCode)}`)
            .then((r) => setReferrerName(r.data?.referrer_name || null))
            .catch(() => setReferrerName(null));
    }, [refCode]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await register(name, email, password, refCode || null);
        setLoading(false);
        if (res.ok) navigate("/app");
        else setError(res.error);
    };

    return (
        <main className="container-page py-16 max-w-md">
            <h1 className="text-4xl font-semibold text-[#1C2321] mb-2" style={{ fontFamily: "Outfit" }}>Start reading</h1>
            <p className="text-[#515B57] text-lg mb-8">Five minutes a day. That's all it takes to begin.</p>

            {referrerName && (
                <div
                    className="mb-6 rounded-xl border border-[#E5E7E2] bg-[#F5F1E4] p-4 flex items-start gap-3"
                    data-testid="register-referral-banner"
                    style={{ borderLeft: "4px solid #D47A55" }}
                >
                    <Gift className="w-5 h-5 text-[#D47A55] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                    <div>
                        <p className="text-[#1C2321] font-medium">
                            {referrerName} invited you to Lift Up Reading.
                        </p>
                        <p className="text-sm text-[#515B57] mt-1">
                            When you become a member, they'll earn an extra 30 free days on us.
                        </p>
                    </div>
                </div>
            )}

            <form onSubmit={submit} className="space-y-5" data-testid="register-form">
                <div>
                    <label className="block text-sm font-medium text-[#1C2321] mb-2">Your first name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        autoComplete="given-name"
                        data-testid="register-name-input"
                        className="w-full px-4 py-3 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] text-lg focus:outline-none focus:ring-2 focus:ring-[#2D5D4E]"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-[#1C2321] mb-2">Email</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                        data-testid="register-email-input"
                        className="w-full px-4 py-3 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] text-lg focus:outline-none focus:ring-2 focus:ring-[#2D5D4E]"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-[#1C2321] mb-2">Password</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        autoComplete="new-password"
                        data-testid="register-password-input"
                        className="w-full px-4 py-3 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] text-lg focus:outline-none focus:ring-2 focus:ring-[#2D5D4E]"
                    />
                    <p className="text-sm text-[#819888] mt-2">At least 6 characters.</p>
                </div>
                {error && (
                    <p className="text-[#B24C4C] text-base" data-testid="register-error">{error}</p>
                )}
                <p className="text-sm text-[#515B57]" data-testid="register-consent">
                    By creating an account, you agree to our{" "}
                    <Link to="/terms" className="underline text-[#2D5D4E] hover:text-[#1F4237]" data-testid="register-consent-terms">
                        Terms
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy" className="underline text-[#2D5D4E] hover:text-[#1F4237]" data-testid="register-consent-privacy">
                        Privacy Policy
                    </Link>
                    .
                </p>
                <button
                    type="submit"
                    disabled={loading}
                    data-testid="register-submit-button"
                    className="w-full py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237] transition-colors disabled:opacity-50"
                >
                    {loading ? "Creating account…" : "Create account"}
                </button>
            </form>

            <p className="mt-8 text-[#515B57]">
                Already have one?{" "}
                <Link to="/login" className="text-[#2D5D4E] font-medium hover:underline" data-testid="register-go-login">
                    Sign in
                </Link>
            </p>
        </main>
    );
}
